import {MeshCellNoteId} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {MeshCellSettingsId} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocationKey} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {
    AnySheetSettings,
    SheetSettingsId
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettingsTypes';
import {SheetId, SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {createTransformer, ITransformer} from 'mobx-utils';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';

export interface ISheetSettingsSnapshot
{
    id:SheetSettingsId;
    slotId:SlotId;
    sheetId:SheetId,
    cellSettings:{[key:string]:MeshCellSettingsId};
    cellNotes:{[key:string]:MeshCellNoteId};
    cellFormats:{[key:string]:string};
}

export class SheetSettings extends AnySheetSettings
{
    private constructor()
    {
        super();
    }

    public static create(id:SheetSettingsId = IdUtil.newGUID(),
                         sheetId:SheetId,
                         slotId:SlotId,
                         cellSettings:Map<MeshLocationKey, MeshCellSettingsId> = new Map(),
                         cellNotes:Map<MeshLocationKey, MeshCellNoteId> = new Map(),
                         cellFormats:Map<MeshLocationKey, string> = new Map()):SheetSettings
    {
        const sheetSettings:SheetSettings = new SheetSettings();

        sheetSettings.id = id;
        sheetSettings.sheetId = sheetId;
        sheetSettings.slotId = slotId;
        sheetSettings.cellSettings = cellSettings;
        sheetSettings.cellNotes = cellNotes;
        sheetSettings.cellFormats = cellFormats;

        return sheetSettings;
    }

    public getCellSettings(key:MeshLocationKey):MeshCellSettingsId | null
    {
        return this.cellSettings.get(key);
    }

    public getCellNote(key:MeshLocationKey):MeshCellNoteId | null
    {
        return this.cellNotes.get(key);
    }

    public getCellFormat(key:MeshLocationKey):string | null
    {
        return this.cellFormats.get(key);
    }

    public setCellSettings(key:MeshLocationKey, cellSettingsId:MeshCellSettingsId):void
    {
        this.cellSettings.set(key, cellSettingsId);
    }

    public removeCellSettings(key:MeshLocationKey):boolean
    {
        return this.cellSettings.delete(key);
    }

    public setCellNote(key:MeshLocationKey, cellNoteId:MeshCellNoteId):void
    {
        this.cellNotes.set(key, cellNoteId);
    }

    public removeCellNote(key:MeshLocationKey):boolean
    {
        return this.cellNotes.delete(key);
    }

    public removeCellFormat(key:MeshLocationKey):boolean
    {
        return this.cellFormats.delete(key);
    }

    public setCellFormat(key:MeshLocationKey, cellFormatId:string):void
    {
        this.cellFormats.set(key, cellFormatId);
    }

    public static toJson:ITransformer<SheetSettings, ISheetSettingsSnapshot> = createTransformer((sheetSettings:SheetSettings) =>
    {
        return {
            id: sheetSettings.id,
            slotId: sheetSettings.slotId,
            sheetId: sheetSettings.sheetId,
            cellSettings: Array.from(sheetSettings.cellSettings.entries())
                .reduce((result, [meshLocationKey, meshCellSettingsId]) =>
                {
                    result[meshLocationKey] = meshCellSettingsId;
                    return result;
                }, {}),
            cellNotes: Array.from(sheetSettings.cellNotes.entries())
                .reduce((result, [meshLocationKey, meshCellNoteId]) =>
                {
                    result[meshLocationKey] = meshCellNoteId;
                    return result;
                }, {}),
            cellFormats: Array.from(sheetSettings.cellFormats.entries())
                .reduce((result, [meshLocationKey, meshCellFormatId]) =>
                {
                    result[meshLocationKey] = meshCellFormatId;
                    return result;
                }, {})
        };
    });

    public static fromJson(json:ISheetSettingsSnapshot,
                           currentState?:SheetSettings,
                           currentJson?:ISheetSettingsSnapshot,
                           ctx?:DesignStateFromJsonCtx):SheetSettings
    {
        if( !currentState )
            return SheetSettings.fromJsonDeep(json);

        if( !currentJson )
            currentJson = SheetSettings.toJson(currentState);

        const sheetSettings:SheetSettings = new SheetSettings();

        sheetSettings.id = json.id;
        sheetSettings.sheetId = json.sheetId;
        sheetSettings.slotId = json.slotId;

        if( json.cellSettings === currentJson.cellSettings )
            sheetSettings.cellSettings = currentState.cellSettings;
        else
        {
            const cellSettings:Map<MeshLocationKey, MeshCellSettingsId> = new Map();

            Object.entries(json.cellSettings)
                .forEach(([meshLocationKey, meshCellSettingsId]) => cellSettings.set(meshLocationKey, meshCellSettingsId));

            sheetSettings.cellSettings = cellSettings;
        }

        if( json.cellNotes === currentJson.cellNotes )
            sheetSettings.cellNotes = currentState.cellNotes;
        else
        {
            const cellNotes:Map<MeshLocationKey, MeshCellNoteId> = new Map();

            Object.entries(json.cellNotes)
                .forEach(([meshLocationKey, meshCellNoteId]) => cellNotes.set(meshLocationKey, meshCellNoteId));

            sheetSettings.cellNotes = cellNotes;
        }

        if( json.cellFormats === currentJson.cellFormats )
            sheetSettings.cellFormats = currentState.cellFormats;
        else
        {
            const cellFormats:Map<MeshLocationKey, string> = new Map();

            Object.entries(json.cellFormats)
                .forEach(([meshLocationKey, meshCellFormatId]) => cellFormats.set(meshLocationKey, meshCellFormatId));

            sheetSettings.cellFormats = cellFormats;
        }

        return sheetSettings;
    }

    public static fromJsonDeep(json:ISheetSettingsSnapshot):SheetSettings
    {
        const sheetSettings:SheetSettings = new SheetSettings();

        sheetSettings.id = json.id;
        sheetSettings.sheetId = json.sheetId;
        sheetSettings.slotId = json.slotId;

        const cellSettings:Map<MeshLocationKey, MeshCellSettingsId> = new Map();
        const cellNotes:Map<MeshLocationKey, MeshCellNoteId> = new Map();
        const cellFormats:Map<MeshLocationKey, string> = new Map();

        Object.entries(json.cellSettings)
            .forEach(([meshLocationKey, meshCellSettingsId]) => cellSettings.set(meshLocationKey, meshCellSettingsId));

        Object.entries(json.cellNotes)
            .forEach(([meshLocationKey, meshCellNoteId]) => cellNotes.set(meshLocationKey, meshCellNoteId));

        Object.entries(json.cellFormats)
            .forEach(([meshLocationKey, meshCellFormatId]) => cellFormats.set(meshLocationKey, meshCellFormatId));

        sheetSettings.cellSettings = cellSettings;
        sheetSettings.cellNotes = cellNotes;
        sheetSettings.cellFormats = cellFormats;

        return sheetSettings;
    }
}
